import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Heart, Share2, ChevronLeft, ChevronRight, Star, ShoppingCart } from 'lucide-react';
import { useCart } from '@/lib/cart';
import type { Product } from '@/types';

interface QuickViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
}

export function QuickViewModal({ isOpen, onClose, product }: QuickViewModalProps) {
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedColor, setSelectedColor] = useState(0);
  const [selectedStorage, setSelectedStorage] = useState(0);
  const { addItem, openCart } = useCart();

  if (!product) return null;

  const colors = product.tags.filter((t) => t.startsWith('color:')).map((t) => t.replace('color:', ''));
  const storageOptions = product.tags.filter((t) => t.startsWith('storage:')).map((t) => t.replace('storage:', ''));

  const discount = product.compareAtPrice
    ? Math.round(((product.compareAtPrice - product.price) / product.compareAtPrice) * 100)
    : 0;

  const handleAddToCart = () => {
    addItem({
      productId: product.id,
      slug: product.slug,
      name: product.name,
      price: product.price,
      compareAtPrice: product.compareAtPrice,
      image: product.images[0]?.url || '',
      quantity: 1,
      maxQuantity: product.inventory,
    });
    openCart();
    onClose();
  };

  const nextImage = () => {
    setSelectedImage((prev) => (prev + 1) % product.images.length);
  };

  const prevImage = () => {
    setSelectedImage((prev) => (prev - 1 + product.images.length) % product.images.length);
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-full md:max-w-4xl md:max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl z-50 overflow-hidden flex flex-col md:flex-row"
            role="dialog"
            aria-modal="true"
            aria-label={`Quick view: ${product.name}`}
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 z-10 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white dark:hover:bg-gray-800 transition-colors"
              aria-label="Close quick view"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="relative w-full md:w-1/2 bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
              <div className="relative w-full aspect-square">
                <img
                  src={product.images[selectedImage]?.url || product.images[0]?.url}
                  alt={product.images[selectedImage]?.alt || product.name}
                  className="w-full h-full object-cover"
                />
              </div>

              {product.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white transition-colors"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/80 dark:bg-gray-800/80 hover:bg-white transition-colors"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              )}

              {discount > 0 && (
                <span className="absolute top-4 left-4 px-2 py-1 bg-primary-600 text-white text-xs font-bold rounded-lg">
                  -{discount}%
                </span>
              )}
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">
                    {product.category.name}
                  </p>
                  <h2 className="text-xl font-bold mt-1">{product.name}</h2>
                </div>
                <div className="flex gap-2">
                  <button
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Add to wishlist"
                  >
                    <Heart className="w-5 h-5" />
                  </button>
                  <button
                    className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    aria-label="Share product"
                  >
                    <Share2 className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 mt-3">
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      className={`w-4 h-4 ${
                        star <= Math.round(product.rating)
                          ? 'fill-yellow-400 text-yellow-400'
                          : 'text-gray-300'
                      }`}
                    />
                  ))}
                </div>
                <span className="text-sm text-muted-foreground">
                  {product.rating} ({product.reviewCount} reviews)
                </span>
                <span className="text-sm text-muted-foreground">
                  {product.soldCount} sold
                </span>
              </div>

              <div className="mt-4 flex items-baseline gap-3">
                <span className="text-2xl font-bold text-primary-600">
                  S${product.price.toFixed(2)}
                </span>
                {product.compareAtPrice && (
                  <span className="text-lg text-muted-foreground line-through">
                    S${product.compareAtPrice.toFixed(2)}
                  </span>
                )}
              </div>

              {colors.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">
                    Color: <span className="text-muted-foreground">{colors[selectedColor]}</span>
                  </p>
                  <div className="flex gap-2">
                    {colors.map((color, index) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(index)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          selectedColor === index
                            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-600'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {storageOptions.length > 0 && (
                <div className="mt-4">
                  <p className="text-sm font-medium mb-2">Storage:</p>
                  <div className="flex gap-2">
                    {storageOptions.map((option, index) => (
                      <button
                        key={option}
                        onClick={() => setSelectedStorage(index)}
                        className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                          selectedStorage === index
                            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/20 text-primary-600'
                            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <p className="text-sm">
                  Stock:{' '}
                  <span
                    className={`font-medium ${
                      product.inventory > 0 ? 'text-green-600' : 'text-destructive'
                    }`}
                  >
                    {product.inventory > 0 ? `${product.inventory} available` : 'Out of stock'}
                  </span>
                </p>
              </div>

              <button
                onClick={handleAddToCart}
                disabled={product.inventory === 0}
                className="mt-6 w-full py-3 bg-primary-600 text-white font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <ShoppingCart className="w-5 h-5" />
                Add to Cart
              </button>

              <p className="mt-3 text-xs text-muted-foreground text-center">
                Free shipping on orders over SGD 50
              </p>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
